import { FC, KeyboardEvent } from 'react';

import { withTheme } from '@emotion/react';
import styled from '@emotion/styled';
import {
  compose,
  variant,
  space,
  display,
  flex,
  layout,
  DisplayProps,
  FlexProps,
  LayoutProps,
  SpaceProps,
  ThemeProps
} from '@lucid/theme';

import CardAction from './card-action';
import CardActions from './card-actions';
import CardContent from './card-content';
import CardImage from './card-image';
import CardSubtitle from './card-subtitle';
import CardTitle from './card-title';

export type CardVariant = 'interactive' | 'raised';
export const cardVariants = ['interactive', 'raised'];
export interface CardProps
  extends ThemeProps,
    DisplayProps,
    FlexProps,
    LayoutProps,
    SpaceProps {
      /** Sets the HTML tag for the Card component. */
      as?: 'div' | 'section' | 'article';
      /** Allows the component to get additional styles. */
      className?: string;
      /** Sets the approriate styles if disabled. */
      isDisabled?: boolean;
      /** Passes an onClick function to the Card component. */
      onClick?: () => void;
      /** Passes an onKeyDown function to the Card component. */
      onKeyDown?: (event: KeyboardEvent<Element>) => void;
      /** Sets the role of the Card component. */
      role?: string;
      /** Sets the tabIndex for the Box Component. */
      tabIndex?: number;
      /** The available variants for the Card component. */
      variant?: CardVariant;
}

const cardStyleProps = compose(
  space,
  display,
  layout,
  flex
);

const CardBase: FC<CardProps> = styled.div(
  ({
    theme,
    isDisabled
  }) => ([
    {
      label: 'card',
      background: 'white',
      borderRadius: theme.radii.card,
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: theme.colors.coolGrey[10],
      overflow: 'hidden'
    },
    isDisabled && {
      borderColor: theme.colors.coolGrey[5],
      pointerEvents: 'none'
    }
  ]),
  variant({
    scale: 'card',
    // required to enable the new API
    variants: {
      defaultVariant: {}
    }
  }),
  cardStyleProps
);

const Card = Object.assign(
  withTheme(CardBase),
  {
    Action: CardAction,
    Actions: CardActions,
    Content: CardContent,
    Image: CardImage,
    Title: CardTitle,
    Subtitle: CardSubtitle
  }
);

Card.displayName = 'Card';

export { Card };
export default Card;
